import type { RGBColor } from "react-color";
import type { StyleLibrary } from "schemas/generated/project";

import React, { useState } from "react";

import { PickerComponent } from "@common/designSystem/color/CustomPicker";
import Input from "@common/designSystem/Input";
import { useDesignLibraryColors } from "@editor/components/shops-ui/designLibrary/useDesignLibraryColors";
import useCurrentProjectId from "@editor/hooks/useCurrentProjectId";
import { useDesignLibrary } from "@editor/hooks/useDesignLibrary";
import {
  getFormattedColor,
  getFormattedColorWithoutOpacity,
  getHex8Color,
  getHexColor,
  getInitialColorValue,
  getRGB,
} from "@editor/utils/colors";

import Popover from "@replo/design-system/components/popover/Popover";
import { ToggleGroup } from "@replo/design-system/components/toggle/ToggleGroup";
import { X } from "lucide-react";
import useMeasure from "react-use-measure";
import { useOverridableState } from "replo-runtime/shared/hooks/useOverridableState";
import { useDebouncedCallback } from "replo-utils/react/use-debounced-callback";

const DEFAULT_COLOR = "#FFFFFF";

interface ColorModifierProps {
  value?: string | null;
  onChange: (color: string) => void;
}

interface ColorGridProps {
  styleLibrary: StyleLibrary | null;
  onColorSelect: (color: string) => void;
}

const ColorGrid: React.FC<ColorGridProps> = ({
  styleLibrary,
  onColorSelect,
}) => {
  const { allColors } = useDesignLibraryColors(styleLibrary, {
    includeEmpty: false,
  });

  if (allColors.length === 0) {
    return (
      <div className="flex flex-col justify-center items-center text-center text-muted text-sm w-full">
        <div className="mb-1">No design library colors available</div>
        <div className="text-xs">
          Set up colors in your design library to see them here
        </div>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-3 gap-2 p-1 w-full overflow-y-auto">
      {allColors.map((color) => (
        <button
          key={color.key}
          className="flex flex-col items-center p-1.5 rounded-lg border-[0.5px] border-black/10 hover:bg-surface transition-colors w-full"
          onClick={() => onColorSelect(color.value)}
          title={`${color.name} - ${getFormattedColorWithoutOpacity(color.value)?.toUpperCase()}`}
        >
          <div
            className="w-full aspect-square rounded-lg border-[0.5px] border-black/10 mb-2 max-w-[50px]"
            style={{ backgroundColor: color.value }}
          />
          <div className="flex flex-col items-center text-xs">
            <div className="text-muted font-medium mb-1">{color.name}</div>
            <div className="font-mono text-[10px] text-muted uppercase">
              {getFormattedColorWithoutOpacity(color.value)?.toUpperCase()}
            </div>
          </div>
        </button>
      ))}
    </div>
  );
};

interface ColorTriggerButtonProps {
  currentColor: string;
  disabled?: boolean;
  size?: "sm" | "md";
}

const ColorTriggerButton: React.FC<
  ColorTriggerButtonProps & {
    onClick: () => void;
    onColorChange: (color: string) => void;
  }
> = React.memo(({ currentColor, disabled = false, onClick, onColorChange }) => {
  const colorSwatch = (
    <div
      className="w-4 h-4 rounded border border-border flex-shrink-0"
      style={{ backgroundColor: currentColor }}
    />
  );

  const [inputValue, setInputValue] = useOverridableState(
    currentColor.toUpperCase(),
    React.useCallback(
      (value: string) => {
        onColorChange(value);
      },
      [onColorChange],
    ),
  );

  React.useEffect(() => {
    setInputValue(currentColor.toUpperCase());
  }, [currentColor, setInputValue]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setInputValue(value);
  };

  return (
    <div onClick={onClick} className="w-full">
      <Input
        value={inputValue}
        onChange={handleInputChange}
        size="sm"
        isDisabled={disabled}
        startEnhancer={colorSwatch}
        endEnhancer={() => (
          <X
            size={12}
            className="text-muted hover:text-default transition-colors cursor-pointer"
            onClick={(e) => {
              e.stopPropagation();
              onColorChange("#00000000");
            }}
          />
        )}
        layoutClassName="w-full"
      />
    </div>
  );
});

ColorTriggerButton.displayName = "ColorTriggerButton";

const ColorModifierComponent: React.FC<ColorModifierProps> = ({
  value,
  onChange,
}) => {
  const currentValue = value ?? DEFAULT_COLOR;
  const projectId = useCurrentProjectId();
  const { currentStyleLibrary } = useDesignLibrary(projectId);

  const debouncedOnChange = useDebouncedCallback((color: string) => {
    onChange(color);
  }, 300);

  const [isOpen, setIsOpen] = useState(false);
  const [selectedTab, setSelectedTab] = useState<string>("styles");
  // NOTE (Kurt, 2025-01-27): Only sync external changes when the popover is closed
  // to prevent feedback loops that would cause the picker to "jump" during user interaction.
  // When the user drags the picker, it updates the selected element, which would normally
  // trigger this effect and reset the picker position mid-drag without this guard.
  // Only sync external changes when the popover is closed
  const [triggerRef, { width: triggerWidth }] = useMeasure();

  const [pickerColor, setPickerColor] = useState<RGBColor>(() =>
    getInitialColorValue(currentValue),
  );

  React.useEffect(() => {
    if (!isOpen) {
      const newColor = getRGB(currentValue);
      if (newColor) {
        setPickerColor(newColor);
      }
    }
  }, [currentValue, isOpen]);

  const handleColorChange = React.useCallback(
    (newColor: string) => {
      debouncedOnChange(newColor);
    },
    [debouncedOnChange],
  );

  const handlePickerColorChange = React.useCallback(
    (rgb: RGBColor) => {
      setPickerColor(rgb);
      const hexColor = getFormattedColor(getHexColor(rgb));
      if (hexColor) {
        handleColorChange(hexColor);
      }
    },
    [handleColorChange],
  );

  const handlePickerColorCommit = React.useCallback(() => {
    const hexColor = getFormattedColor(getHex8Color(pickerColor));
    if (hexColor) {
      handleColorChange(hexColor);
    }
  }, [pickerColor, handleColorChange]);

  const handleStyleColorSelect = React.useCallback(
    (color: string) => {
      handleColorChange(color);
      setIsOpen(false);
    },
    [handleColorChange],
  );

  return (
    <div ref={triggerRef} className="w-full">
      <Popover
        isOpen={isOpen}
        onOpenChange={setIsOpen}
        side="top"
        sideOffset={8}
        variant="unstyled"
        shouldPreventDefaultOnInteractOutside={false}
        triggerAsChild
        content={
          <div
            className="flex flex-col items-start gap-3 bg-white border-[0.5px] border-[#CBD5E1] rounded-lg shadow-[4px_4px_16px_rgba(0,0,0,0.06)]"
            style={{ width: triggerWidth || 178 }}
          >
            <div className="w-full p-3 pb-0">
              <ToggleGroup
                size="sm"
                selectedValue={selectedTab}
                onChange={setSelectedTab}
                options={[
                  { value: "styles", label: "Styles" },
                  { value: "custom", label: "Custom" },
                ]}
                layoutClassName="w-full"
              />
            </div>

            {selectedTab === "styles" ? (
              <div className="px-2 pb-2 w-full">
                <ColorGrid
                  styleLibrary={currentStyleLibrary}
                  onColorSelect={handleStyleColorSelect}
                />
              </div>
            ) : (
              <div className="w-full px-2 pb-2">
                <PickerComponent
                  color={pickerColor}
                  onChange={({ rgb }) => handlePickerColorChange(rgb)}
                  onDragEnd={handlePickerColorCommit}
                />
              </div>
            )}
          </div>
        }
      >
        <div className="w-full">
          <ColorTriggerButton
            currentColor={currentValue}
            onClick={() => setIsOpen(!isOpen)}
            onColorChange={handleColorChange}
          />
        </div>
      </Popover>
    </div>
  );
};

export default ColorModifierComponent;
